import { useCallback, useEffect, useState } from "react";
import { Briefcase, Link2, Loader2, RefreshCw, Search, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CollapsibleSection } from "@/components/settings/SettingsUI";
import { getFoundationWoQueue, dismissFoundationWoQueue } from "@/lib/api";
import PushJobSpButton from "./PushJobSpButton";
import FoundationNewCustomerNotice from "./FoundationNewCustomerNotice";
import { FoundationLinkJobDialog } from "./FoundationLinkJobDialog";

const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600", submitted: "bg-sky-100 text-sky-800", approved: "bg-indigo-100 text-indigo-800",
  scheduled: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800",
  invoiced: "bg-violet-100 text-violet-800", paid: "bg-emerald-100 text-emerald-800",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function WoRow({ row, onDone, onDismiss, onCustomerChanged, onLinked, busy }) {
  const failed = row.last_attempt && row.last_attempt.outcome && row.last_attempt.outcome !== "success";
  const newCustomer = row.customer?.is_new;
  const [linkOpen, setLinkOpen] = useState(false);
  const inFdn = row.in_foundation;
  return (
    <div className={`border rounded-lg p-3 bg-white ${failed ? "border-rose-200" : inFdn ? "border-violet-300" : newCustomer ? "border-amber-300" : "border-slate-200"}`}
         data-testid={`foundation-wo-row-${row.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-slate-800" data-testid={`foundation-wo-number-${row.id}`}>{row.wo_number}</span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_CLS[row.status] || "bg-slate-100 text-slate-600"}`}>{row.status}</span>
            {inFdn && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-900 border border-violet-300 inline-flex items-center gap-1"
                    title={`Foundation already has job ${inFdn.job_no}${inFdn.description ? ` — ${inFdn.description}` : ""} (start ${inFdn.job_start_date || "—"}). Use Link only instead of pushing.`}
                    data-testid={`foundation-wo-in-foundation-${row.id}`}>
                <Link2 className="w-2.5 h-2.5" /> Already in Foundation — link it
              </span>
            )}
            {newCustomer && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                    title={row.customer.reason} data-testid={`foundation-wo-new-customer-${row.id}`}>
                New customer — not yet in Foundation
              </span>
            )}
            {row.phase1_only && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                    title="Only the job number and description were pushed (bridge predates v2.3) — re-push to fill the rest"
                    data-testid={`foundation-wo-phase1-${row.id}`}>
                Phase 1 only — needs full re-push
              </span>
            )}
            {row.completion_flag && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300"
                    title={`completion_date ${row.completion_date || ""} → @completion_date (Addl tab · Job End Date)`}
                    data-testid={`foundation-wo-completion-${row.id}`}>
                {row.completion_flag}
              </span>
            )}
            {row.customer_no_changed_since_push && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-900 border border-sky-300"
                    title="The customer's Foundation # changed after this job was pushed — re-push to update it"
                    data-testid={`foundation-wo-cust-changed-${row.id}`}>
                Customer # changed since last push
              </span>
            )}
            {row.foundation_sync_mode === "skipped" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" title="Marked 'keep in-app only' at mark-won — still pushable">in-app only</span>
            )}
            <span className="text-[11px] text-slate-400">created {fmt(row.created_at)}</span>
          </div>
          <div className="text-xs text-slate-700 mt-0.5 truncate">
            {row.title || row.project_name || <span className="text-slate-400">No title</span>}
            {row.client_name && <span className="text-slate-400"> · {row.client_name}</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Foundation will receive: <code className="text-slate-700">{row.preview?.job_no}</code>
            {" — "}<code className="text-slate-700">&quot;{row.preview?.description}&quot;</code>
            {row.preview?.truncated && <span className="text-amber-700"> (truncated to 30)</span>}
            {row.tax_authority && <> · tax <code className="text-slate-700">{row.tax_authority}</code></>}
          </div>
          {row.customer && (newCustomer || row.customer_no_changed_since_push) && (
            <div className="mt-1.5 text-[11px]">
              <FoundationNewCustomerNotice customer={row.customer} onChanged={onCustomerChanged}
                                           allowOverride testIdPrefix={`foundation-wo-${row.id}`} />
            </div>
          )}
          {row.customer && !newCustomer && !row.customer_no_changed_since_push && (
            <details className="mt-1 text-[11px]">
              <summary className="text-slate-500 cursor-pointer hover:text-slate-700" data-testid={`foundation-wo-cust-toggle-${row.id}`}>
                Foundation customer <code className="text-slate-700">{row.customer.foundation_code || "—"}</code>
                {row.customer.foundation_name ? ` · ${row.customer.foundation_name}` : ""} — change
              </summary>
              <div className="mt-1">
                <FoundationNewCustomerNotice customer={row.customer} onChanged={onCustomerChanged}
                                             allowOverride testIdPrefix={`foundation-wo-${row.id}`} />
              </div>
            </details>
          )}
          {row.preview?.errors?.length > 0 && (
            <div className="text-[11px] text-rose-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{row.preview.errors.join(" ")}</div>
          )}
          {failed && (
            <div className="text-[11px] text-rose-700 mt-0.5" data-testid={`foundation-wo-last-attempt-${row.id}`}>
              Last attempt {fmt(row.last_attempt.at)}: {row.last_attempt.message}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!(row.preview?.errors?.length > 0) && (
            <PushJobSpButton woId={row.id} source="approval_queue" label={failed ? "Retry push" : "Approve & push"}
                             testId={`foundation-wo-approve-${row.id}`} onPushed={(res) => onDone(row.id, res)} />
          )}
          <button onClick={() => setLinkOpen(true)} disabled={busy}
                  className={`text-xs px-2 py-1.5 rounded-md border inline-flex items-center gap-1 disabled:opacity-50 ${inFdn ? "border-violet-400 bg-violet-50 text-violet-800 hover:bg-violet-100" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                  title="The job already exists in Foundation — link it without pushing anything"
                  data-testid={`foundation-wo-link-${row.id}`}>
            <Link2 className="w-3 h-3" /> Link only
          </button>
          <button onClick={() => onDismiss(row.id)} disabled={busy}
                  className="text-xs px-2 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                  title="Keep this WO in-app only (removes it from the queue)"
                  data-testid={`foundation-wo-dismiss-${row.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Dismiss
          </button>
        </div>
      </div>
      {linkOpen && <FoundationLinkJobDialog row={row} onClose={() => setLinkOpen(false)} onLinked={(res) => onLinked(row.id, res)} />}
    </div>
  );
}

// Work Orders that have a WO# but no successful Foundation SP push yet.
export default function FoundationWoQueue({ onCountChange }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyCompleted, setOnlyCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async (query, completedOnly = onlyCompleted) => {
    setLoading(true);
    try {
      const { data: d } = await getFoundationWoQueue({ limit: 100, q: query || undefined, filter: completedOnly ? "completed_not_pushed" : undefined });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Work Orders awaiting Foundation");
    } finally {
      setLoading(false);
    }
  }, [onCountChange, onlyCompleted]);

  useEffect(() => {
    const t = setTimeout(() => reload(q), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, onlyCompleted, reload]);

  const dropRow = (id) => setData((d) => d && ({
    ...d, rows: d.rows.filter((r) => r.id !== id), total: Math.max(0, d.total - 1),
  }));

  useEffect(() => { if (data) onCountChange?.(data.total); }, [data, onCountChange]);

  const handleDone = (id, res) => {
    if (res?.outcome === "success") dropRow(id); else reload(q);
  };

  const handleDismiss = async (id) => {
    setBusyId(id);
    try {
      await dismissFoundationWoQueue(id);
      toast.success("Kept in-app only — removed from the Foundation queue");
      dropRow(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    } finally {
      setBusyId(null);
    }
  };

  const rows = data?.rows || [];
  return (
    <CollapsibleSection
      id="foundation-wo-queue" testId="foundation-wo-queue"
      icon={Briefcase} iconClass="text-violet-600"
      title={`Work Orders awaiting Foundation (${data ? data.total : "…"})`}
      actions={
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOnlyCompleted((v) => !v)}
                  className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${onlyCompleted ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                  title="Completed work orders whose completion date has not reached Foundation"
                  data-testid="foundation-wo-queue-filter-completed">
            Completed, not pushed
            <span className={`px-1.5 rounded-full text-[10px] font-semibold ${onlyCompleted ? "bg-white/25" : "bg-emerald-100 text-emerald-800"}`} data-testid="foundation-wo-queue-completed-count">
              {data?.counts?.completed_not_pushed ?? "…"}
            </span>
          </button>
          <div className="relative">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="WO # or title"
                   className="pl-6 pr-2 py-1 text-xs border border-slate-300 rounded-md w-44"
                   data-testid="foundation-wo-queue-search" />
          </div>
          <button onClick={() => reload(q)} disabled={loading}
                  className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="foundation-wo-queue-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      }
    >
      <p className="text-xs text-slate-500 mb-2">
        Every Work Order with a WO# appears here until it has been pushed to Foundation
        (<code>sp_insert_update_jobs</code> on <code>{data?.database || "Cas_16075_Practice"}</code>) or dismissed. Approving runs one confirmed SP call.
        {" "}Already keyed into Foundation by accounting? Use <b>Link only</b> — nothing is pushed and Foundation keeps its own start date and contract.
        {data?.foundation_lookup?.checked === false && data.foundation_lookup.reason && data.foundation_lookup.reason !== "no work orders" && (
          <span className="block text-amber-700 mt-0.5" data-testid="foundation-wo-lookup-skipped">Could not check Foundation for existing jobs — {data.foundation_lookup.reason}.</span>
        )}
      </p>
      {data === null && loading ? (
        <div className="text-center py-6 text-sm text-slate-400" data-testid="foundation-wo-queue-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center text-sm text-emerald-700" data-testid="foundation-wo-queue-empty">
          {q ? "No Work Orders match." : "Every Work Order is in Foundation."}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => <WoRow key={r.id} row={r} onDone={handleDone} onDismiss={handleDismiss} onLinked={(id) => dropRow(id)}
                                  onCustomerChanged={() => reload(q)} busy={busyId === r.id} />)}
          {data.total > rows.length && (
            <div className="text-[11px] text-slate-400 text-center">Showing newest {rows.length} of {data.total} — search to narrow.</div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
